import React from 'react';
import { Star } from 'lucide-react';

interface ServerData {
  name: string;
  covers: number;
  revenue?: number;
  avgCheck: number;
  tips: number;
  upsellRate: number;
  rating: number;
  turnTime?: number;
}

interface ServerLeaderboardProps {
  data: ServerData[];
  loading?: boolean;
}

const ServerLeaderboard: React.FC<ServerLeaderboardProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="cc-leaderboard">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="cc-loading-row">
            <div className="cc-loading-shimmer" style={{ width: '100%', height: 44, borderRadius: 6 }} />
          </div>
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="cc-leaderboard cc-leaderboard--empty">
        <p className="cc-muted-text">No server performance data available</p>
      </div>
    );
  }

  const getRankStyle = (index: number): string => {
    if (index === 0) return 'cc-rank--gold';
    if (index === 1) return 'cc-rank--silver';
    if (index === 2) return 'cc-rank--bronze';
    return 'cc-rank--default';
  };

  const getUpsellColor = (rate: number): string => {
    if (rate >= 35) return '#10b981';
    if (rate >= 25) return '#06b6d4';
    return '#f59e0b';
  };

  return (
    <div className="cc-leaderboard">
      <div style={{ overflowX: 'auto' }}>
        <table className="cc-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'center', width: 60 }}>Rank</th>
              <th>Server</th>
              <th>Covers</th>
              <th>Avg Check</th>
              <th>Tips</th>
              <th>Upsell %</th>
              <th>Guest Rating</th>
            </tr>
          </thead>
          <tbody>
            {data.map((server, i) => (
              <tr key={i} className={i === 0 ? 'cc-table__row--highlight' : ''}>
                <td style={{ textAlign: 'center' }}>
                  <span className={`cc-rank-badge ${getRankStyle(i)}`}>
                    {i + 1}
                  </span>
                </td>
                <td className="cc-table__cell--name">{server.name}</td>
                <td className="cc-table__cell--secondary">{server.covers}</td>
                <td className="cc-table__cell--green">${server.avgCheck.toFixed(2)}</td>
                <td className="cc-table__cell--amber">${server.tips.toLocaleString()}</td>
                <td>
                  <div className="cc-upsell-bar">
                    <div className="cc-upsell-bar__track">
                      <div
                        className="cc-upsell-bar__fill"
                        style={{
                          width: `${Math.min(server.upsellRate, 100)}%`,
                          backgroundColor: getUpsellColor(server.upsellRate),
                        }}
                      />
                    </div>
                    <span className="cc-upsell-bar__label">{server.upsellRate}%</span>
                  </div>
                </td>
                <td>
                  <div className="cc-rating-cell">
                    <Star size={12} fill="#f59e0b" color="#f59e0b" />
                    <span className="cc-rating-cell__value">{server.rating}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServerLeaderboard;
